import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Input, notification } from "antd";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, CheckIcon, FileText, Video } from "lucide-react";
import {
  DeleteOutlined,
  SendOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";

import Secondnsection from "../../assets/images/ZPPD_30s_v03_viewing.mp4";
import Galaxy from "../../assets/images/Galaxy Tab S8 Ultra.png";
import newIphone from "../../assets/images/newiphone.png";
import AwardCarousel from "./AwardsCarousel";
import VideoRecordModal from "../../components/Modals/VideoRecordModal";
import DemoModal from "../../components/Modals/DemoModal";
import PublicLayout from "../../components/layout/PublicLayout";
import {
  useCreatecontactResponseMutation,
  useCreateSupportMutation,
} from "../../store/api/apiSlice";
import DemoForm from "./DemoForm";
import { SOLUTIONS } from "../../constants/landing";
import {
  buildContactRequestPayload,
  scrollToSectionRef,
} from "../../utils/landing";

const { TextArea } = Input;

const LandingPage = () => {
  const [form] = Form.useForm();
  const [supportForm] = Form.useForm();
  const [createResponse, { isLoading: isContactSubmitting }] =
    useCreatecontactResponseMutation();
  const [createSupport, { isLoading: isSupportSubmitting }] =
    useCreateSupportMutation();

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [activeContactTab, setActiveContactTab] = useState("contact");
  const [videoUrl, setVideoUrl] = useState(null);
  const [supportFormKey, setSupportFormKey] = useState(0);

  const location = useLocation();

  const contactRef = useRef(null);
  const featuresRef = useRef(null);
  const whyUnzippdRef = useRef(null);

  const setDemoModalVisibility = (isOpen) => setIsDemoModalOpen(isOpen);

  const setVideoModalVisibility = (isOpen) => setIsVideoModalOpen(isOpen);

  const handleVideoSaved = (url) => setVideoUrl(url);

  const handleFeaturesClick = () => scrollToSectionRef(featuresRef);

  const handleWhyUnzippdClick = () => scrollToSectionRef(whyUnzippdRef);

  const handleContactClick = () => scrollToSectionRef(contactRef);

  const handleContactFormSubmit = async (values) => {
    try {
      await createResponse(
        buildContactRequestPayload(values, videoUrl),
      ).unwrap();

      notification.success({
        message: "Success",
        description: "Your message has been sent successfully!",
      });

      form.resetFields();

      setVideoUrl(null);

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      // API slice already handles error notifications for failed requests.
    }
  };

  const handleSupportFormSubmit = async (values) => {
    try {
      await createSupport({
        full_name: values.fullName,
        email: values.email,
        subject: values.subject,
        ...(videoUrl ? { video: videoUrl } : {}),
      }).unwrap();

      notification.success({
        message: "Success",
        description: "Your support request has been sent successfully!",
      });

      supportForm.resetFields();

      handleDeleteVideo();

      setSupportFormKey((prev) => prev + 1);

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Support form error:", error);
    }
  };

  const handleDeleteVideo = () => {
    setVideoUrl(null);
    setVideoModalVisibility(false);
  };

  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.replace("#", "");
    const target = document.getElementById(id);
    if (!target) return;

    const headerOffset = 88;
    const top =
      target.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
  }, [location.hash]);

  return (
    <PublicLayout
      onFeaturesClick={handleFeaturesClick}
      onWhyUnzippdClick={handleWhyUnzippdClick}
      onContactClick={handleContactClick}
      onDemoClick={() => setDemoModalVisibility(true)}
      showDemoButton
    >
      <div className="bg-[#222222] text-white px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10">
        <div className=" mx-auto max-w-7xl py-12 sm:py-20">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="mb-10 sm:mb-16">
                <h1 className="text-5xl font-bold leading-tight mb-4">
                  The UK's First
                  <div>
                    <span className="bg-blue my-1">Two-Way BSL</span>
                  </div>
                  Research Platform.
                </h1>
                <p className="text-xl text-gray-200 mb-8">
                  The insights you need. The way Deaf people want to give them.
                </p>
                <Button
                  onClick={() => setDemoModalVisibility(true)}
                  type="primary"
                  className="bg-blue rounded-2xl border-none hover:bg-[#ff4a1a]"
                >
                  Get a Demo
                  <ArrowUpRight size={20} />
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <video
                autoPlay
                loop
                muted
                src={Secondnsection}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>
          </div>
          <br />
          <br />
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-blue text-xl font-semibold mb-4">Insights</h2>
              <p className="text-gray-300 leading-relaxed">
                Unzippd is the ultimate platform for gaining insights into the
                Deaf experience. Whether your goal is improving brand
                engagement, enhancing accessibility or empowering community
                voices, a two-way communication platform ensures every
                interaction drives meaningful impact. Collect, analyse and act
                on insights in real time from waiting rooms, workplaces, or
                public spaces.
              </p>
            </div>
            <div>
              <h2 className="text-blue text-xl font-semibold mb-4">
                Deaf Experience
              </h2>
              <p className="text-gray-300 leading-relaxed">
                <span className="text-white">
                  "9 in 10 Deaf people surveyed, do not respond to surveys
                  written in English."
                </span>
                <br />
                To connect and be understood is at the heart of being human.
                Deaf people speak up when the dialogue is in BSL; intuitive,
                effortless, and authentic. That is what we do.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div
        id="features"
        className="bg-[#222222] text-white py-0"
        ref={featuresRef}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 sm:gap-16 items-center">
            <div className="space-y-8">
              <h2 className="text-4xl font-bold mb-6">The Unzippd Platform</h2>
              <div className="space-y-4">
                <p
                  className=" text-gray-200"
                  style={{
                    lineHeight: 1.5,
                  }}
                >
                  The most groundbreaking, inclusive, and human feedback
                  platform in the UK. A first-of-its-kind platform for{" "}
                  <span className="bg-blue px-2 py-1">
                    both asking and answering questions in BSL.
                  </span>{" "}
                  It delivers seamless, inclusive, and scalable solutions for
                  businesses and organisations alike.
                </p>
              </div>
              <div className="space-y-2 mt-8 border p-3 rounded-2xl">
                <div className="flex items-start gap-4 ">
                  <div className="w-12 h-12 rounded-full border border-[#FF5C35] flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-blue"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M9 3H15M3 9V15M21 9V15M9 21H15M12 12H12.01M19 12H19.01M5 12H5.01M12 5V19"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-blue text-lg font-semibold my-0">
                      Assisted Machine Learning Translation:
                    </h3>
                    <p className="text-gray-300 text-sm my-2">
                      Accurate British Sign Language interpretation powered by
                      advanced machine learning.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full border border-[#FF5C35] flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-blue"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 6V12M12 12V18M12 12H18M12 12H6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-blue text-lg font-semibold my-0">
                      Customisable Integration:
                    </h3>
                    <p className="text-gray-300 text-sm my-2">
                      APIs and tailored solutions to fit your business needs
                      effortlessly.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full border border-[#FF5C35] flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-blue"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M8 12H8.01M12 12H12.01M16 12H16.01M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-blue text-lg font-semibold my-0">
                      Feedback Mechanism:
                    </h3>
                    <p className="text-gray-300 text-sm my-2">
                      User-friendly, video-based tools for accessible feedback
                      and engagement.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full border border-[#FF5C35] flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-blue"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 15V17M6 21H18C19.1046 21 20 20.1046 20 19V13C20 11.8954 19.1046 11 18 11H6C4.89543 11 4 11.8954 4 13V19C4 20.1046 4.89543 21 6 21ZM16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11H16Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-blue text-lg font-semibold my-0">
                      Data Privacy First:
                    </h3>
                    <p className="text-gray-300 text-sm my-2">
                      Fully secure and compliant systems, ensuring user trust
                      and confidence.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full border border-[#FF5C35] flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-blue"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M8 16V8M12 16V4M16 16V12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-blue text-lg font-semibold my-0">
                      Analytics and Insights:
                    </h3>
                    <p className="text-gray-300 text-sm my-2">
                      Actionable data for organisations to enhance inclusivity
                      and customer experience.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative flex items-center justify-center">
              <div>
                <img
                  src={Galaxy}
                  alt="Unzippd Platform Interface"
                  className="w-full max-w-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#222222] text-white py-8 sm:py-10 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12  items-center">
            <div className="relative flex justify-center items-center">
              <div className="w-64 md:w-80">
                <div className="md:mt-[120px]"></div>
                <img
                  src={newIphone}
                  alt="Mobile Interface"
                  className="w-full h-auto"
                />
              </div>
            </div>
            <div>
              <h2 className="text-blue text-3xl font-bold mb-8 text-center">
                Flexible Solutions
              </h2>
              <div className="  flex flex-wrap gap-3 rounded-2xl py-3 justify-center">
                {SOLUTIONS.map((solution, index) => (
                  <div
                    key={index}
                    className="bg-transparent border  rounded-full px-6 py-2.5 text-white cursor-pointer transition-colors duration-300"
                  >
                    {solution}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        id="why-unzippd"
        className="bg-[#222222] text-white pt-5 pb-12 sm:py-20"
        ref={whyUnzippdRef}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-4xl font-bold mb-12 text-blue">
            Why Choose Unzippd?
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mb-16 sm:mb-32">
            <div className="bg-[#D0D0D0] rounded-2xl ">
              <div className="items-center text-center justify-center rounded-t-xl gap-2 py-1 flex bg-blue">
                <div className="flex items-center justify-center">
                  <Video
                    className="h-4 w-4 md:h-5 md:w-5 text-white"
                    aria-hidden
                  />
                </div>
                <span className=" font-semibold text-sm md:text-xl py-3">
                  Unzippd
                </span>
              </div>
              <div className="space-y-6 p-4 pt-0">
                <div className="">
                  <h3 className="font-semibold text-sm mb-2 text-black">
                    Survey Expertise:
                    <span className="text-black text-sm font-light">
                      {" "}
                      Transform surveys into tools that deliver actionable,
                      high-quality insights.
                    </span>
                  </h3>
                </div>
                <div className="">
                  <h3 className="font-semibold text-sm mb-2 text-black">
                    Proven Tools:
                    <span className="text-black text-sm font-light">
                      {" "}
                      Research-backed library of validated questions for
                      reliable and impactful data.
                    </span>
                  </h3>
                </div>
                <div className="">
                  <h3 className="font-semibold text-sm mb-2 text-black">
                    Seamless Communication:
                    <span className="text-black text-sm font-light">
                      {" "}
                      Intuitive two-way interaction with Deaf users in BSL for
                      asking and answering questions.
                    </span>
                  </h3>
                </div>
                <div className="">
                  <h3 className="font-semibold text-sm mb-2 text-black">
                    Cutting-Edge Technology:
                    <span className="text-black text-sm font-light">
                      {" "}
                      State-of-the-art translation with feedback insights
                      powered by Assisted Machine Learning.
                    </span>
                  </h3>
                </div>
                <div className="">
                  <h3 className="font-semibold text-sm mb-2 text-black">
                    Impactful Results:
                    <span className="text-black text-sm font-light">
                      {" "}
                      Real-time dashboards offering insights into results and
                      engagement.
                    </span>
                  </h3>
                </div>
              </div>
            </div>
            <div className="bg-[#D0D0D0] rounded-2xl">
              <div className="p-2 items-center text-center justify-center rounded-t-xl gap-2 py-1 flex bg-black">
                <div className="flex items-center justify-center">
                  <FileText
                    className="h-4 w-4 md:h-5 md:w-5 text-white"
                    aria-hidden
                  />
                </div>
                <span className="font-semibold text-sm md:text-xl py-3">
                  Direct Competitors (written forms)
                </span>
              </div>
              <div className="space-y-6 p-4 pt-0">
                <div>
                  <h3 className="font-semibold text-sm mb-2 text-black">
                    Generic Surveys:
                    <span className="text-black text-sm font-light">
                      {" "}
                      Limited expertise behind survey design, leading to
                      low-quality data.
                    </span>
                  </h3>
                </div>
                <div className="">
                  <h3 className="font-semibold text-sm mb-2 text-black">
                    No Standards:
                    <span className="text-black text-sm font-light">
                      {" "}
                      Lack of research-backed questions, reducing credibility
                      and impact.
                    </span>
                  </h3>
                </div>
                <div className="">
                  <h3 className="font-semibold text-sm mb-2 text-black">
                    Restricted Communication:
                    <span className="text-black text-sm font-light">
                      {" "}
                      Limited or no interaction with Deaf users, relying on
                      inaccessible written formats.
                    </span>
                  </h3>
                </div>
                <div className="">
                  <h3 className="font-semibold text-sm mb-2 text-black">
                    Outdated Tools:
                    <span className="text-black text-sm font-light">
                      {" "}
                      No integration with AI, machine learning, or modern
                      translation solutions.
                    </span>
                  </h3>
                </div>
                <div className="">
                  <h3 className="font-semibold text-sm mb-2 text-black">
                    Static Results:
                    <span className="text-black text-sm font-light">
                      {" "}
                      Basic exports without real-time insights or actionable
                      outputs.
                    </span>
                  </h3>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-blue">
              Customer Testimonial
            </h2>
            <div className="relative">
              <h3 className="text-4xl md:text-5xl font-bold leading-tight mb-8">
                <span className="text-blue mr-3">"</span>I love how{" "}
                <span className="bg-blue px-2">easy</span> it is to respond in
                sign language, all in just a click of a button
                <span className="text-blue ml-3">"</span>
              </h3>
            </div>
            <p className="text-blue mt-8">Katzi - Deaf User</p>
          </div>
        </div>
      </div>
      <AwardCarousel />
      <div
        id="contact-us"
        ref={contactRef}
        className="bg-[#1A1A1A] text-white py-12 sm:py-24"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-8 sm:mb-12">
            Contact Us
          </h2>
          <div className="rounded-2xl p-4 sm:p-8 bg-[#333333]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div className="flex bg-[#fff] w-full sm:w-fit p-1 rounded-full test">
                <button
                  type="button"
                  onClick={() => setActiveContactTab("contact")}
                  className={`w-1/2 px-6 py-2 rounded-full text-sm font-medium transition-colors border-0 text-center whitespace-nowrap cursor-pointer ${
                    activeContactTab === "contact"
                      ? "bg-[#222222] text-white"
                      : "bg-transparent text-black"
                  }`}
                >
                  Contact
                </button>
                <button
                  type="button"
                  onClick={() => setActiveContactTab("demo")}
                  className={`w-1/2 px-6 py-2 rounded-full text-sm font-medium transition-colors border-0 text-center whitespace-nowrap cursor-pointer ${
                    activeContactTab === "demo"
                      ? "bg-[#222222] text-white"
                      : "bg-transparent text-black"
                  }`}
                >
                  Get a Demo
                </button>
              </div>
              <button
                type="button"
                onClick={() => setActiveContactTab("support")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors text-center whitespace-nowrap cursor-pointer ${
                  activeContactTab === "support"
                    ? "bg-[#222222] text-white border border-white"
                    : "bg-white text-[#222222] border-0"
                }`}
              >
                Support
              </button>
            </div>
            {activeContactTab === "contact" ? (
              <Form
                form={form}
                onFinish={handleContactFormSubmit}
                layout="vertical"
                className="space-y-4"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <Form.Item
                    label={<span className="text-white">Full Name</span>}
                    name="fullName"
                    className="mb-0 rounded-lg"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your full name",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Type here..."
                      className="bg-white rounded-lg h-11 px-4 w-full border-none"
                    />
                  </Form.Item>
                  <Form.Item
                    label={<span className="text-white">Email Address</span>}
                    name="email"
                    className="mb-0 rounded-lg"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input
                      prefix={<span className="text-gray-400">✉</span>}
                      placeholder="example@email.com"
                      className="bg-white rounded-lg h-11 px-4 w-full border-none"
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  label={<span className="text-white">Subject</span>}
                  name="subject"
                  className="mb-4"
                  rules={[
                    { required: !videoUrl, message: "Please enter a subject" },
                  ]}
                >
                  <Input
                    placeholder="Type here..."
                    className="bg-white rounded-lg h-11 px-4 w-full border-none"
                  />
                </Form.Item>
                <Form.Item
                  label={<span className="text-white">Your Message</span>}
                  name="message"
                  className="mb-6"
                  rules={[
                    {
                      required: !videoUrl,
                      message: "Please enter your message",
                    },
                  ]}
                >
                  <TextArea
                    placeholder="Write your message here..."
                    rows={6}
                    className="bg-white rounded-lg p-3 w-full border-none resize-none"
                  />
                </Form.Item>
                {videoUrl && (
                  <Form.Item
                    label={<span className="text-white">Video Message</span>}
                    name="video"
                    className="mb-4"
                  >
                    {videoUrl ? (
                      <div className="flex items-center justify-between bg-white rounded-lg p-3">
                        <span className="text-gray-700">
                          Video recorded successfully
                        </span>
                        <DeleteOutlined onClick={handleDeleteVideo} />
                      </div>
                    ) : null}
                  </Form.Item>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  <Button
                    htmlType="submit"
                    loading={isContactSubmitting}
                    disabled={isContactSubmitting}
                    className="h-12 w-full bg-[#222222] text-white border-0 px-6 rounded-xl flex items-center justify-center gap-2 hover:bg-opacity-90 transition-colors"
                  >
                    Send Message
                    <SendOutlined className="ml-2" />
                  </Button>
                  <button
                    type="button"
                    onClick={() => setVideoModalVisibility(true)}
                    className="h-12 w-full bg-blue text-white border-0 px-6 rounded-xl flex items-center justify-center gap-2 hover:bg-opacity-90 transition-colors"
                  >
                    Record a BSL Video
                    <VideoCameraOutlined className="ml-2" />
                  </button>
                </div>
              </Form>
            ) : activeContactTab === "support" ? (
              <Form
                key={supportFormKey}
                form={supportForm}
                onFinish={handleSupportFormSubmit}
                layout="vertical"
                className="space-y-4"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <Form.Item
                    label={<span className="text-white">Full Name</span>}
                    name="fullName"
                    className="mb-0 rounded-lg"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your full name",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Type here..."
                      className="bg-white rounded-lg h-11 px-4 w-full border-none"
                    />
                  </Form.Item>
                  <Form.Item
                    label={<span className="text-white">Email Address</span>}
                    name="email"
                    className="mb-0 rounded-lg"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input
                      prefix={<span className="text-gray-400">✉</span>}
                      placeholder="example@email.com"
                      className="bg-white rounded-lg h-11 px-4 w-full border-none"
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  label={<span className="text-white">Subject</span>}
                  name="subject"
                  className="mb-6"
                  rules={[
                    { required: true, message: "Please enter a subject" },
                  ]}
                >
                  <TextArea
                    placeholder="Type here..."
                    rows={6}
                    className="bg-white rounded-lg p-3 w-full border-none resize-none"
                  />
                </Form.Item>
                <div className="grid md:grid-cols-2 gap-4">
                  <Button
                    htmlType="submit"
                    loading={isSupportSubmitting}
                    disabled={isSupportSubmitting}
                    className="h-12 w-full bg-[#222222] text-white border-0 px-6 rounded-xl flex items-center justify-center gap-2 hover:bg-opacity-90 transition-colors"
                  >
                    Submit Request
                    <CheckIcon />
                  </Button>
                  <button
                    type="button"
                    onClick={() => setVideoModalVisibility(true)}
                    className="h-12 w-full bg-blue text-white border-0 px-6 rounded-xl flex items-center justify-center gap-2 hover:bg-opacity-90 transition-colors"
                  >
                    Record a BSL Video
                    <VideoCameraOutlined className="ml-2" />
                  </button>
                </div>
              </Form>
            ) : (
              <DemoForm />
            )}
          </div>
        </div>
      </div>
      <VideoRecordModal
        isOpen={isVideoModalOpen}
        onClose={() => setVideoModalVisibility(false)}
        onVideoSave={handleVideoSaved}
        videoUrl={videoUrl}
      />
      <DemoModal
        isOpen={isDemoModalOpen}
        onClose={() => setDemoModalVisibility(false)}
      />
    </PublicLayout>
  );
};

export default LandingPage;
